import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { db } from "./firebase-config";
import { ordinal } from "./ordinal";

// Remaining-time marks (ms) at which a fact about each of the two players is
// revealed into the shared chat. Spaced a minute apart on a 3-minute game.
export const REVEAL_STAGES = [
  { id: "reveal-2min", thresholdMs: 120000 },
  { id: "reveal-1min", thresholdMs: 60000 },
];

// Simple deterministic string hash — used instead of Math.random() so both
// players' clients independently compute the exact same "random" pick
// (which player gets revealed first, which fact gets chosen) without
// needing to coordinate or have one side dictate the other's UI.
export function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function dateKeyDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Builds the pool of true, currently-applicable facts about a player, as
// structured {type, ...params} objects rather than finished sentences. The
// same fact reads as "you've played 6 games" to its own subject and
// "they've played 6 games" to the other player — and since the *subject* is
// fixed but the *viewer* differs per client, the grammatical person has to
// be resolved at render time (see renderFact) rather than baked into the
// text once and shared. Otherwise whichever client wins the write race would
// freeze in its own perspective for both players.
export async function buildFactCandidates(profile, uid) {
  const facts = [];
  const history = profile.gameHistory || {};
  const monthKey = new Date().toISOString().slice(0, 7);
  const todayKey = dateKeyDaysAgo(0);

  if (!profile.gamesPlayed) {
    facts.push({ type: "first_game" });
  }

  const yesterday = history[dateKeyDaysAgo(1)];
  if (yesterday) {
    facts.push({ type: "yesterday_move", move: yesterday.myMove });
  }

  let monthCoop = 0;
  let monthDefect = 0;
  Object.entries(history).forEach(([key, g]) => {
    if (key.startsWith(monthKey)) {
      if (g.myMove === "cooperate") monthCoop++;
      else if (g.myMove === "defect") monthDefect++;
    }
  });
  if (monthCoop + monthDefect >= 2) {
    facts.push({ type: "month_balance", coop: monthCoop, defect: monthDefect });
  }

  if (profile.gamesPlayed) {
    facts.push({ type: "all_time_games", games: profile.gamesPlayed });
    facts.push({ type: "all_time_points", points: profile.totalScore || 0 });
  }

  const pastDates = Object.keys(history).filter((k) => k !== todayKey).sort();
  const lastKey = pastDates[pastDates.length - 1];
  if (lastKey) {
    facts.push({ type: "last_move", move: history[lastKey].myMove });
  }

  try {
    const usersQuery = query(collection(db, "users"), orderBy("totalScore", "desc"), limit(100));
    const snapshot = await getDocs(usersQuery);
    const scored = snapshot.docs
      .map((d) => {
        const data = d.data();
        const h = data.gameHistory || {};
        let score = 0;
        let games = 0;
        Object.entries(h).forEach(([k, g]) => {
          if (k.startsWith(monthKey)) {
            score += g.myScore || 0;
            games++;
          }
        });
        return { uid: d.id, score, games };
      })
      .filter((u) => u.games > 0)
      .sort((a, b) => b.score - a.score);
    const rank = scored.findIndex((u) => u.uid === uid) + 1;
    if (rank > 0) facts.push({ type: "month_rank", rank });
  } catch (error) {
    console.error("Error computing rank fact:", error);
  }

  return facts;
}

// Turns a structured fact into a sentence, in second person ("you've...")
// if the viewer IS the subject, third person ("they've...") otherwise.
export function renderFact(fact, isMe) {
  if (!fact) return "";
  const subj = isMe ? "you" : "they";
  const poss = isMe ? "your" : "their";
  const are = isMe ? "you're" : "they're";

  switch (fact.type) {
    case "first_game":
      return `this is ${poss} first game!`;
    case "yesterday_move":
      return `${subj} ${fact.move === "defect" ? "betrayed" : "trusted"} yesterday`;
    case "month_balance":
      if (fact.defect > fact.coop) return `this month, ${subj}'ve betrayed more than ${subj}'ve trusted`;
      if (fact.coop > fact.defect) return `this month, ${subj}'ve trusted more than ${subj}'ve betrayed`;
      return `this month, ${are} split evenly between trust and betrayal`;
    case "all_time_games":
      return `${subj}'ve played ${fact.games} games all-time`;
    case "all_time_points":
      return `${subj}'ve racked up ${fact.points} points all-time`;
    case "last_move":
      return `${poss} last move was ${fact.move === "defect" ? "a betrayal" : "trust"}`;
    case "month_rank":
      return `${are} ranked ${ordinal(fact.rank)} this month`;
    default:
      return "";
  }
}
